/**
 * Shared, presentational Progress Note form used by both Shift Detail and the
 * Participant Profile. It owns NO context (no shift lookup, no API, no auth).
 * Parents supply `form`, `onChange`, and action handlers.
 */
import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export const NOTE_TEMPLATE_KEYS = ['DAILY_SUPPORT', 'COMMUNITY_ACCESS', 'PERSONAL_CARE', 'BEHAVIOUR_SUPPORT', 'OTHER'];

export function ProgressNoteForm({ form, onChange }) {
  const set = (k, v) => onChange({ ...form, [k]: v });
  return (
    <div className="space-y-3" data-testid="progress-note-form">
      <div className="space-y-1">
        <Label className="text-xs">Template</Label>
        <Select value={form.template_key} onValueChange={(v) => set('template_key', v)}>
          <SelectTrigger className="h-9 text-sm" data-testid="note-template"><SelectValue /></SelectTrigger>
          <SelectContent>
            {NOTE_TEMPLATE_KEYS.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Note</Label>
        <textarea className="w-full min-h-[80px] rounded-lg border bg-background px-3 py-2 text-sm" value={form.note} onChange={(e) => set('note', e.target.value)} placeholder="Describe the support provided..." data-testid="note-text" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label className="text-xs">Goals Worked Toward</Label><Input className="h-9 text-sm" value={form.goals_worked_toward} onChange={(e) => set('goals_worked_toward', e.target.value)} data-testid="note-goals" /></div>
        <div className="space-y-1"><Label className="text-xs">Outcomes</Label><Input className="h-9 text-sm" value={form.outcomes} onChange={(e) => set('outcomes', e.target.value)} data-testid="note-outcomes" /></div>
      </div>
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-xs"><Checkbox checked={form.risks_identified} onCheckedChange={(v) => set('risks_identified', !!v)} data-testid="note-risks-check" /> Risks identified</label>
        <label className="flex items-center gap-2 text-xs"><Checkbox checked={form.requires_follow_up} onCheckedChange={(v) => set('requires_follow_up', !!v)} data-testid="note-followup-check" /> Requires follow-up</label>
      </div>
      {form.risks_identified && <Input className="h-9 text-sm" value={form.risk_details} onChange={(e) => set('risk_details', e.target.value)} placeholder="Risk details..." data-testid="note-risk-details" />}
      {form.requires_follow_up && <Input className="h-9 text-sm" value={form.follow_up_details} onChange={(e) => set('follow_up_details', e.target.value)} placeholder="Follow-up details..." data-testid="note-followup-details" />}
    </div>
  );
}

export const emptyNoteForm = () => ({
  template_key: 'DAILY_SUPPORT', note: '', goals_worked_toward: '', outcomes: '',
  risks_identified: false, risk_details: '', requires_follow_up: false, follow_up_details: '',
});

export default ProgressNoteForm;
